import React, { useState, useEffect } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import ApplicationModal from './ApplicationModal'; // Import your existing modal

const JobListings = ({ onApplyClick }) => {
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch job data from Statamic
  useEffect(() => {
    const fetchJobData = async () => {
      try {
        const response = await fetch('/api/careers');
        if (!response.ok) {
          throw new Error('Failed to fetch job data');
        }
        const data = await response.json();
        
        // Transform Statamic data to match our component structure
        if (data.current_openings && Array.isArray(data.current_openings)) {
          const formattedJobs = data.current_openings
            .filter(job => job.enabled !== false)
            .map(job => ({
              id: job.id,
              title: job.job_title,
              department: job.department,
              location: formatLocation(job.location),
              type: formatJobType(job.job_type),
              experience: job.experience,
              salary: job.salary_range,
              description: job.job_description?.[0]?.text || 'No description available',
              requirements: job.requirements?.map(req => req.requirement) || [],
              benefits: job.benefits?.map(benefit => benefit.benefit) || [],
              applyLink: job.apply_now_link || null // Add apply link from Statamic
            }));
          
          setJobOpenings(formattedJobs);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching job data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobData();
  }, []);

  // Helper function to format location
  const formatLocation = (location) => {
    if (!location) return 'Nairobi';
    
    const locationMap = {
      'eastleigh_nairobi': 'Eastleigh, Nairobi',
      'nairobi': 'Nairobi',
      'mombasa': 'Mombasa',
      'kisumu': 'Kisumu'
    };
    
    return locationMap[location] || location.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to format job type
  const formatJobType = (jobType) => {
    if (!jobType) return 'Full-time';
    
    const typeMap = {
      'full_time': 'Full-time',
      'part_time': 'Part-time',
      'contract': 'Contract',
      'freelance': 'Freelance'
    };
    
    return typeMap[jobType] || jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const toggleJobDetails = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleApply = (job) => {
    // If job has a specific apply link, redirect to it
    if (job.applyLink) {
      window.open(job.applyLink, '_blank');
      return;
    }
    
    // Otherwise, open the application modal
    setSelectedJob(job);
    setIsApplicationModalOpen(true);
    
    // Also call the parent component's handler if provided
    onApplyClick?.(job.title);
  };

  if (loading) {
    return (
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#eb2227]/5 via-white to-[#08005f]/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#08005f] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading job openings...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#eb2227]/5 via-white to-[#08005f]/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center text-red-600">
            <p>Error loading job openings: {error}</p>
            <Button
              className="mt-4 bg-[#08005f] text-white hover:bg-red-600"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#eb2227]/5 via-white to-[#08005f]/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center p-3 bg-[#eb2227]/10 rounded-full mb-6">
                <Icon name="Briefcase" size={28} className="text-[#eb2227]" />
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#08005f] mb-6">
                Current{' '}
                <span className="bg-gradient-to-r from-[#08005f] to-[#08005f] bg-clip-text text-transparent">
                  Openings
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                Discover opportunities to grow your career in healthcare with us. Find your perfect role
                and start making a difference today.
              </p>
            </div>

            {/* Job Cards */}
            <div className="space-y-8">
              {jobOpenings?.map((job) => (
                <div
                  key={job?.id}
                  className="relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-[1.01] overflow-hidden border border-gray-100"
                >
                  <div className="p-6 md:p-8 relative">
                    {/* Job Header */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-2xl font-bold text-[#08005f] group-hover:text-[#eb2227] transition-colors duration-300">
                        {job?.title}
                      </h3>
                      <span className="px-3 py-1 bg-[#08005f]/10 text-[#08005f] text-sm font-medium rounded-full">
                        {job?.department}
                      </span>
                      {job?.salary && (
                        <span className="px-3 py-1 bg-[#eb2227]/10 text-[#eb2227] text-sm font-medium rounded-full">
                          {job?.salary}
                        </span>
                      )}
                    </div>

                    {/* Job Meta */}
                    <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="MapPin" size={16} className="text-[#eb2227]" />
                        <span className="font-medium">{job?.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={16} className="text-[#eb2227]" />
                        <span className="font-medium">{job?.type}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Award" size={16} className="text-[#eb2227]" />
                        <span className="font-medium">{job?.experience}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed mb-6">{job?.description}</p>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleJobDetails(job?.id)}
                        className="flex items-center justify-center space-x-2 border-[#08005f] text-[#08005f] hover:bg-[#08005f]/10"
                      >
                        <Icon
                          name={expandedJob === job?.id ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                        />
                        <span>{expandedJob === job?.id ? 'Hide Details' : 'View Details'}</span>
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleApply(job)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-[#08005f] to-[#08005f] hover:from-[#eb2227]/90 hover:to-[#eb2227]/90 text-white shadow-md hover:shadow-lg"
                      >
                        <Icon name="Send" size={16} />
                        <span>Apply Now</span>
                      </Button>
                    </div>

                    {/* Expanded Details */}
                    {expandedJob === job?.id && (
                      <div className="mt-8 pt-8 border-t border-gray-200 animate-fadeIn">
                        <div className="grid md:grid-cols-2 gap-8">
                          {/* Requirements */}
                          <div>
                            <h4 className="font-bold text-[#08005f] mb-4 flex items-center space-x-2">
                              <Icon name="FileText" size={18} className="text-[#eb2227]" />
                              <span>Requirements</span>
                            </h4>
                            <ul className="space-y-3">
                              {job?.requirements?.map((req, index) => (
                                <li
                                  key={index}
                                  className="flex items-start space-x-3 text-gray-600"
                                >
                                  <Icon
                                    name="CheckCircle"
                                    size={16}
                                    className="text-[#eb2227] mt-0.5 flex-shrink-0"
                                  />
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Benefits */}
                          <div>
                            <h4 className="font-bold text-[#08005f] mb-4 flex items-center space-x-2">
                              <Icon name="Gift" size={18} className="text-[#eb2227]" />
                              <span>Benefits</span>
                            </h4>
                            <ul className="space-y-3">
                              {job?.benefits?.map((benefit, index) => (
                                <li
                                  key={index}
                                  className="flex items-start space-x-3 text-gray-600"
                                >
                                  <Icon
                                    name="Star"
                                    size={16}
                                    className="text-[#08005f] mt-0.5 flex-shrink-0"
                                  />
                                  <span>{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Apply CTA */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <div className="bg-gradient-to-r from-[#eb2227]/10 to-[#08005f]/10 rounded-xl p-6 text-center">
                            <p className="text-gray-600 mb-4">
                              Ready to join our team? Submit your application and we'll get back to
                              you within 3 business days.
                            </p>
                            <Button
                              variant="default"
                              onClick={() => handleApply(job)}
                              className="bg-gradient-to-r from-[#08005f] to-[#08005f] hover:from-[#eb2227]/90 hover:to-[#eb2227]/90 text-white shadow-md hover:shadow-lg"
                            >
                              <Icon name="Send" size={16} className="mr-2" />
                              Apply for this Position
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* No Jobs Available */}
            {jobOpenings?.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Search" size={32} className="text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-[#08005f] mb-4">No Open Positions</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  We don't have any open positions at the moment, but we're always looking for
                  talented individuals to join our team.
                </p>
                 {/*<Button
                  variant="outline"
                  onClick={() => handleApply({ title: 'General Application' })}
                  className="border-[#eb2227] text-[#eb2227] hover:bg-[#eb2227]/10"
                >
                  Submit General Application
                </Button> */}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Application Modal */}
      <ApplicationModal
        isOpen={isApplicationModalOpen}
        onClose={() => setIsApplicationModalOpen(false)}
        selectedPosition={selectedJob?.title || ''}
      />
    </>
  );
};

export default JobListings;